import { useDatoClient } from '@src/stores-v2/StoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useMemo, useRef, useState } from 'react';

import type React from 'react';

const logger = LogManager.getLogger('hook:useSpeakerFormHeadshots');

interface HeadshotEntry {
  id: string;
  url: string;
}

interface SpeakerHeadshotInput {
  headshot?: string | null;
  headshots?: HeadshotEntry[];
}

export interface SpeakerFormHeadshots {
  /** Combined preview URLs (existing + new local blobs) for rendering */
  allHeadshotPreviews: string[];
  /** Number of existing (already-saved) headshots */
  existingHeadshotsCount: number;
  /** Whether an upload is currently in progress */
  isUploadingHeadshot: boolean;
  /** Ref for the hidden file input element */
  headshotInputRef: React.RefObject<HTMLInputElement>;
  /** Handle file input change — queues new files for upload */
  handleHeadshotSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Remove an existing headshot by index */
  handleRemoveExisting: (index: number) => void;
  /** Remove a queued new headshot by index */
  handleRemoveNew: (index: number) => void;
  /** Trigger the hidden file input click */
  triggerFileInput: () => void;
  /**
   * Upload any new files and return the merged headshot array,
   * or `undefined` if nothing changed, or `null` if all headshots were cleared.
   */
  uploadHeadshotsIfNeeded: () => Promise<HeadshotEntry[] | null | undefined>;
  /** Reset headshot state to initial values. Call from the parent's reset useEffect. */
  reset: () => void;
}

/**
 * Encapsulates all headshot-related state and logic for the SpeakerFormModal:
 * file queuing, preview URLs, existing/new headshot diffing, and upload orchestration.
 *
 * Does NOT manage its own reset timing — exposes `reset` and `resetDeps` so the
 * parent's single useEffect can coordinate all form resets together.
 */
export function useSpeakerFormHeadshots(
  speakerToEdit?: SpeakerHeadshotInput,
): SpeakerFormHeadshots {
  const datoClientStore = useDatoClient();
  const speakerHeadshots = speakerToEdit?.headshots;
  const speakerHeadshot = speakerToEdit?.headshot;

  const initialExisting = useMemo(
    () =>
      speakerHeadshots ||
      (speakerHeadshot ? [{ id: '', url: speakerHeadshot }] : []),
    [speakerHeadshots, speakerHeadshot],
  );

  const [existingHeadshots, setExistingHeadshots] =
    useState<HeadshotEntry[]>(initialExisting);
  const [newHeadshotFiles, setNewHeadshotFiles] = useState<File[]>([]);
  const [newHeadshotPreviews, setNewHeadshotPreviews] = useState<string[]>([]);
  const [isUploadingHeadshot, setIsUploadingHeadshot] = useState(false);
  const headshotInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setExistingHeadshots(initialExisting);
    setNewHeadshotFiles([]);
    setNewHeadshotPreviews([]);
    setIsUploadingHeadshot(false);
  }, [initialExisting]);

  const handleHeadshotSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files) {
        const fileArray = Array.from(files);
        setNewHeadshotFiles(prev => [...prev, ...fileArray]);
        setNewHeadshotPreviews(prev => [
          ...prev,
          ...fileArray.map(f => URL.createObjectURL(f)),
        ]);
      }
      // Reset input so the same file can be selected again
      if (headshotInputRef.current) {
        headshotInputRef.current.value = '';
      }
    },
    [],
  );

  const handleRemoveExisting = useCallback((index: number) => {
    setExistingHeadshots(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleRemoveNew = useCallback((index: number) => {
    setNewHeadshotFiles(prev => prev.filter((_, i) => i !== index));
    setNewHeadshotPreviews(prev => prev.filter((_, i) => i !== index));
  }, []);

  const triggerFileInput = useCallback(() => {
    headshotInputRef.current?.click();
  }, []);

  const allHeadshotPreviews = useMemo(
    () => [...existingHeadshots.map(h => h.url), ...newHeadshotPreviews],
    [existingHeadshots, newHeadshotPreviews],
  );

  const uploadHeadshotsIfNeeded = useCallback(async (): Promise<
    HeadshotEntry[] | null | undefined
  > => {
    const speakerRepo = datoClientStore?.speakerRepository;

    // Determine if existing headshots were removed
    const originalHeadshots =
      speakerHeadshots ||
      (speakerHeadshot ? [{ id: '', url: speakerHeadshot }] : []);
    const existingChanged =
      existingHeadshots.length !== originalHeadshots.length ||
      existingHeadshots.some((h, i) => h.url !== originalHeadshots[i]?.url);
    const hasNewFiles = newHeadshotFiles.length > 0;

    if (!existingChanged && !hasNewFiles) return undefined; // no change

    // If all headshots removed and no new files, clear the gallery
    if (existingHeadshots.length === 0 && !hasNewFiles) return null;

    setIsUploadingHeadshot(true);
    try {
      // Existing headshots already have IDs — no re-fetch needed
      const result: HeadshotEntry[] = [...existingHeadshots.filter(h => h.id)];

      // Upload new files in parallel
      if (hasNewFiles) {
        const uploadFn = speakerRepo
          ? (file: File) => speakerRepo.uploadHeadshot(file)
          : null;

        if (uploadFn) {
          const uploads = await Promise.all(
            newHeadshotFiles.map(file => uploadFn(file)),
          );
          for (const upload of uploads) {
            if (upload) {
              result.push(upload);
            }
          }
        }
      }

      return result.length > 0
        ? result
        : existingHeadshots.length > 0
          ? undefined
          : null;
    } catch (err) {
      logger.error('Failed to upload headshots', extractErrorInfo(err));
      return undefined;
    } finally {
      setIsUploadingHeadshot(false);
    }
  }, [
    datoClientStore,
    existingHeadshots,
    newHeadshotFiles,
    speakerHeadshots,
    speakerHeadshot,
  ]);

  return {
    allHeadshotPreviews,
    existingHeadshotsCount: existingHeadshots.length,
    isUploadingHeadshot,
    headshotInputRef,
    handleHeadshotSelect,
    handleRemoveExisting,
    handleRemoveNew,
    triggerFileInput,
    uploadHeadshotsIfNeeded,
    reset,
  };
}
